import { Pencil, Plug, Trash2 } from 'lucide-react';
import { useT } from '@kmgestao/i18n';
import { Badge, Button, PageSectionCard, Skeleton, toast } from '@kmgestao/ui';

import {
  useProviders,
  useTestProvider,
  type ProviderRow,
} from '../../../../features/ai-admin/index.js';

export interface ProvidersListProps {
  onAdd: () => void;
  onEdit: (provider: ProviderRow) => void;
  onDelete: (provider: ProviderRow) => void;
}

export function ProvidersList({ onAdd, onEdit, onDelete }: ProvidersListProps) {
  const { t } = useT('ai-admin');
  const { data: providers, isLoading, isError, error } = useProviders();

  return (
    <PageSectionCard
      title={t('providers.title')}
      description={t('providers.description')}
      action={
        <Button onClick={onAdd} className="w-full sm:w-auto">
          {t('providers.add')}
        </Button>
      }
    >
      {isLoading ? (
        <ProvidersSkeleton />
      ) : isError ? (
        <p className="text-sm text-destructive">
          {error instanceof Error ? error.message : 'Erro ao carregar.'}
        </p>
      ) : !providers || providers.length === 0 ? (
        <ProvidersEmpty />
      ) : (
        <ul className="space-y-3">
          {providers.map((p) => (
            <ProviderListItem
              key={p.id}
              provider={p}
              onEdit={() => onEdit(p)}
              onDelete={() => onDelete(p)}
            />
          ))}
        </ul>
      )}
    </PageSectionCard>
  );
}

function ProvidersSkeleton() {
  return (
    <ul className="space-y-4">
      {[0, 1, 2].map((i) => (
        <li key={i} className="flex items-center gap-3">
          <Skeleton className="size-10 rounded-full" />
          <div className="flex-1 space-y-2">
            <Skeleton className="h-4 w-1/3" />
            <Skeleton className="h-3 w-2/3" />
          </div>
        </li>
      ))}
    </ul>
  );
}

function ProvidersEmpty() {
  const { t } = useT('ai-admin');
  return (
    <div className="rounded-xl border border-dashed border-border bg-muted/20 p-6 text-center">
      <p className="text-sm font-medium">{t('providers.empty.title')}</p>
      <p className="mt-2 text-sm leading-6 text-muted-foreground">
        {t('providers.empty.description')}
      </p>
    </div>
  );
}

function ProviderListItem({
  provider,
  onEdit,
  onDelete,
}: {
  provider: ProviderRow;
  onEdit: () => void;
  onDelete: () => void;
}) {
  const { t } = useT('ai-admin');
  const test = useTestProvider();

  const handleTest = async () => {
    try {
      const res = await test.mutateAsync({
        providerId: provider.id,
        ...(provider.primaryModel?.modelId ? { modelId: provider.primaryModel.modelId } : {}),
      });
      if (res.ok) {
        toast.success(t('test.successTitle'), {
          description: t('test.successDescription', {
            latency: res.latencyMs,
            model: res.model,
          }),
        });
      } else {
        toast.error(t('test.errorTitle'), {
          description: t('test.errorDescription', { error: res.error ?? '' }),
        });
      }
    } catch (err) {
      toast.error(t('test.errorTitle'), {
        description: err instanceof Error ? err.message : '',
      });
    }
  };

  const apiKeyLabel = provider.apiKeyLast4
    ? t('providers.apiKey.configured', { last4: provider.apiKeyLast4 })
    : t('providers.apiKey.notConfigured');

  return (
    <li className="rounded-xl border border-border/70 bg-card p-4 shadow-sm">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
        <div className="min-w-0 flex-1 space-y-1">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-base font-semibold">{provider.displayName}</span>
            <Badge variant="outline">{provider.key}</Badge>
            {!provider.enabled ? (
              <Badge variant="outline">{t('providers.badges.disabled')}</Badge>
            ) : null}
            {!provider.apiKeyLast4 ? (
              <Badge variant="destructive">{t('providers.badges.keyMissing')}</Badge>
            ) : null}
          </div>
          <div className="grid grid-cols-1 gap-1 text-sm text-muted-foreground sm:grid-cols-[auto_1fr] sm:gap-x-3">
            <span>{t('providers.columns.model')}:</span>
            <span className="font-mono truncate">{provider.primaryModel?.modelId ?? '—'}</span>
            <span>{t('providers.columns.baseUrl')}:</span>
            <span className="font-mono truncate">{provider.baseUrl ?? '—'}</span>
            <span>{t('providers.columns.apiKey')}:</span>
            <span className="font-mono truncate">{apiKeyLabel}</span>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2 lg:shrink-0">
          <Button type="button" variant="outline" onClick={handleTest} disabled={test.isPending}>
            <Plug aria-hidden="true" />
            {test.isPending ? t('test.running') : t('providers.actions.test')}
          </Button>
          <Button type="button" variant="secondary" onClick={onEdit}>
            <Pencil aria-hidden="true" />
            {t('providers.actions.edit')}
          </Button>
          <Button type="button" variant="destructive" onClick={onDelete}>
            <Trash2 aria-hidden="true" />
            {t('providers.actions.delete')}
          </Button>
        </div>
      </div>
    </li>
  );
}
